import { Handle, Position } from '@xyflow/react';
import { StyleSheet, css } from 'aphrodite';
import { formatFilterCondition } from '../utils/filterDisplay';
import type * as Api from '@ViewModels';
import type { BaseNodeData } from '../types/nodes';

export interface ConditionNodeData extends BaseNodeData {
	condition?: Api.AutomationTemplateFilter | Api.IContactFilterCriteria;
	caseIndex?: number;
	isDefault?: boolean;
}

interface ConditionNodeProps {
	data: ConditionNodeData;
	selected?: boolean;
}

const BRANCH_COLORS = [
	'#3B82F6', // Blue
	'#10B981', // Green
	'#F59E0B', // Amber
	'#EF4444', // Red
	'#8B5CF6', // Purple
	'#EC4899', // Pink
	'#14B8A6', // Teal
	'#F97316', // Orange
];

export function ConditionNode({ data, selected }: ConditionNodeProps) {
	const { label, condition, caseIndex = 0, isDefault = false } = data;

	const branchColor = isDefault ? '#6B7280' : BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
	const styles = [styleSheet.container];

	if (selected) {
		styles.push(styleSheet.selected);
	}

	return (
		<div className={css(...styles)} style={{ borderColor: branchColor }}>
			{/* Target handle (input) */}
			<Handle type='target' position={Position.Top} id='target' style={{ background: branchColor }} />

			{/* Diamond/badge indicator */}
			<div className={css(styleSheet.header)} style={{ backgroundColor: branchColor }}>
				<svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
					{isDefault ? (
						// Dots icon for default
						<>
							<circle cx='4' cy='8' r='1.5' fill='white' />
							<circle cx='8' cy='8' r='1.5' fill='white' />
							<circle cx='12' cy='8' r='1.5' fill='white' />
						</>
					) : (
						// Diamond shape for conditional
						<path d='M8 2L14 8L8 14L2 8L8 2Z' fill='white' />
					)}
				</svg>
				<span className={css(styleSheet.headerText)}>{label}</span>
			</div>

			{/* Condition details */}
			{!isDefault && condition && (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.conditionLabel)}>If:</div>
					<div className={css(styleSheet.conditionDetails)}>
						{formatFilterCondition(condition as Api.AutomationTemplateFilter)}
					</div>
				</div>
			)}

			{isDefault && (
				<div className={css(styleSheet.content)}>
					<div className={css(styleSheet.defaultText)}>All other contacts</div>
				</div>
			)}

			{/* Source handle (output) */}
			<Handle type='source' position={Position.Bottom} id='default' style={{ background: branchColor }} />
		</div>
	);
}

const styleSheet = StyleSheet.create({
	container: {
		minWidth: 200,
		maxWidth: 300,
		backgroundColor: '#FFFFFF',
		border: '2px solid #6B7280',
		borderRadius: 8,
		boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
		fontSize: 12,
		transition: 'all 0.2s',
	},
	selected: {
		boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
		transform: 'scale(1.02)',
	},
	header: {
		padding: '8px 12px',
		backgroundColor: '#6B7280',
		color: '#FFFFFF',
		borderRadius: '6px 6px 0 0',
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontWeight: 600,
		fontSize: 12,
	},
	headerText: {
		flex: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	content: {
		padding: 12,
	},
	conditionLabel: {
		fontSize: 10,
		fontWeight: 600,
		color: '#6B7280',
		marginBottom: 4,
		textTransform: 'uppercase',
	},
	conditionDetails: {
		fontSize: 11,
		color: '#374151',
	},
	defaultText: {
		fontSize: 11,
		color: '#6B7280',
		fontStyle: 'italic',
		textAlign: 'center',
	},
});
